import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FaBuilding, FaPlus, FaSearch, FaTrash, FaEdit } from 'react-icons/fa';
import Navbar from '../student/Navbar';
import Loader from '../components/Loader';
import ConfirmModal from '../components/ConfirmModal';
import SuccessModal from '../components/SuccessModal';
import { useAuth } from '../context/AuthContext';
import useCachedApi, { CacheKeys, CacheTTL } from '../hooks/useCachedApi';
import SuperAdminSidebar from './SuperAdminSidebar';

const Clinics = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [filteredClinics, setFilteredClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [clinicToDelete, setClinicToDelete] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successTitle, setSuccessTitle] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { cachedGet, invalidate } = useCachedApi();

  const [formData, setFormData] = useState({
    clinicId: '',
    name: { en: '', ar: '' },
    description: { en: '', ar: '' },
    specialization: { en: '', ar: '' },
    services: [{ en: '', ar: '' }],
    address: {
      street: { en: '', ar: '' },
      city: { en: '', ar: '' },
      country: { en: '', ar: '' },
      postalCode: ''
    },
    contactInfo: {
      phone: '',
      email: '',
      website: ''
    },
    image: '',
    logo: '',
    mapUrl: '',
    workingHours: {
      monday: '',
      tuesday: '',
      wednesday: '',
      thursday: '',
      friday: '',
      saturday: '',
      sunday: ''
    }
  });

  useEffect(() => {
    const fetchClinics = async () => {
      if (!user || !token) {
        setError('Please log in to view clinics.');
        setLoading(false);
        return;
      }

      try {
        const data = await cachedGet('/api/clinics', CacheKeys.CLINICS(), CacheTTL.LONG);
        const list = Array.isArray(data) ? data : [];
        setClinics(list);
        setFilteredClinics(list);
      } catch (err) {
        const errorMessage = err.response?.status === 401
          ? 'Unauthorized. Please log in again.'
          : err.response?.data?.message || 'Failed to load clinics';
        setError(errorMessage);
        if (err.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchClinics();
  }, [user, token, navigate]);

  useEffect(() => {
    const filtered = clinics.filter(clinic => {
      const matchesSearch = 
        clinic.clinicId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clinic.name?.en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clinic.name?.ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clinic.specialization?.en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clinic.specialization?.ar?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
    setFilteredClinics(filtered);
  }, [searchTerm, clinics]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleBilingualChange = (field, language, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [language]: value
      }
    }));
  };

  const handleAddressChange = (field, language, value) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: language ? { ...prev.address[field], [language]: value } : value
      }
    }));
  };

  const handleWorkingHoursChange = (day, value) => {
    setFormData(prev => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: value
      }
    }));
  };

  const addService = () => {
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, { en: '', ar: '' }]
    }));
  };

  const removeService = (index) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  const handleServiceChange = (index, language, value) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.map((service, i) => 
        i === index ? { ...service, [language]: value } : service
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (showEditModal) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/clinics/${selectedClinic.id}`, formData, config);
        setSuccessTitle('Clinic Updated');
        setSuccessMessage('Clinic has been updated successfully!');
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/clinics`, formData, config);
        setSuccessTitle('Clinic Added');
        setSuccessMessage('Clinic has been added successfully!');
      }
      invalidate(CacheKeys.CLINICS());
      setShowModal(false);
      setShowEditModal(false);
      setFormData({
        clinicId: '',
        name: { en: '', ar: '' },
        description: { en: '', ar: '' },
        specialization: { en: '', ar: '' },
        services: [{ en: '', ar: '' }],
        address: {
          street: { en: '', ar: '' },
          city: { en: '', ar: '' },
          country: { en: '', ar: '' },
          postalCode: ''
        },
        contactInfo: {
          phone: '',
          email: '',
          website: ''
        },
        image: '',
        logo: '',
        mapUrl: '',
        workingHours: {
          monday: '',
          tuesday: '',
          wednesday: '',
          thursday: '',
          friday: '',
          saturday: '',
          sunday: ''
        }
      });
      setError('');
      
      // Refresh clinics
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/clinics`, config);
      setClinics(response.data || []);
      
      setShowSuccessModal(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save clinic');
    }
  };

  const handleEdit = (clinic) => {
    setSelectedClinic(clinic);
    setFormData({
      clinicId: clinic.clinicId || '',
      name: clinic.name || { en: '', ar: '' },
      description: clinic.description || { en: '', ar: '' },
      specialization: clinic.specialization || { en: '', ar: '' },
      services: clinic.services || [{ en: '', ar: '' }],
      address: clinic.address || {
        street: { en: '', ar: '' },
        city: { en: '', ar: '' },
        country: { en: '', ar: '' },
        postalCode: ''
      },
      contactInfo: clinic.contactInfo || {
        phone: '',
        email: '',
        website: ''
      },
      image: clinic.image || '',
      logo: clinic.logo || '',
      mapUrl: clinic.mapUrl || '',
      workingHours: clinic.workingHours || {
        monday: '',
        tuesday: '',
        wednesday: '',
        thursday: '',
        friday: '',
        saturday: '',
        sunday: ''
      }
    });
    setShowEditModal(true);
  };

  const handleDelete = (clinic) => {
    setClinicToDelete(clinic);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/clinics/${clinicToDelete.id}`, config);
      invalidate(CacheKeys.CLINICS());
      setClinics(clinics.filter(c => c.id !== clinicToDelete.id));
      setShowConfirmModal(false);
      setClinicToDelete(null);
      
      setSuccessTitle('Clinic Deleted');
      setSuccessMessage('Clinic has been deleted successfully!');
      setShowSuccessModal(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete clinic');
      setShowConfirmModal(false);
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <SuperAdminSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

        <main className="flex-1 overflow-y-auto p-6 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm"
              >
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-3" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <p className="text-red-700 font-medium">{error}</p>
                </div>
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#0077B6] mb-1">Clinics</h1>
                  <p className="text-[#333333]">Manage dental clinics and their information</p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowModal(true)}
                  className="w-full md:w-auto bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white px-6 py-3 rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
                >
                  <FaPlus className="h-5 w-5 mr-2" />
                  Add Clinic
                </motion.button>
              </div>

              <div className="mb-6">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search clinics by ID, name, or specialization..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                  />
                </div>
              </div>

              <motion.div
                variants={container}
                initial="hidden"
                animate="show"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {filteredClinics.length === 0 ? (
                  <div className="col-span-full text-center py-12">
                    <FaBuilding className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No clinics found</h3>
                    <p className="text-gray-500">Add a clinic or adjust your search.</p>
                  </div>
                ) : (
                  filteredClinics.map((clinic) => (
                    <motion.div
                      key={clinic.id}
                      variants={item}
                      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 overflow-hidden"
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">
                              {clinic.name?.en || clinic.name?.ar || 'Unnamed Clinic'}
                            </h3>
                            <p className="text-sm text-gray-500 mb-2">ID: {clinic.clinicId}</p>
                            <p className="text-sm text-gray-600 mb-3">
                              {clinic.specialization?.en || clinic.specialization?.ar || 'No specialization'}
                            </p>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(clinic)}
                              className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                              title="Edit Clinic"
                            >
                              <FaEdit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(clinic)}
                              className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors"
                              title="Delete Clinic"
                            >
                              <FaTrash className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="space-y-2 text-sm text-gray-600">
                          <div className="flex items-center">
                            <span className="font-medium w-20">Phone:</span>
                            <span>{clinic.contactInfo?.phone || 'N/A'}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="font-medium w-20">Email:</span>
                            <span>{clinic.contactInfo?.email || 'N/A'}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="font-medium w-20">City:</span>
                            <span>{clinic.address?.city?.en || clinic.address?.city?.ar || 'N/A'}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="font-medium w-20">Dentists:</span>
                            <span>{clinic.dentists?.length || 0}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="font-medium w-20">Assistants:</span>
                            <span>{clinic.assistants?.length || 0}</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </motion.div>
            </motion.div>
          </div>
        </main>
      </div>

      {/* Add/Edit Clinic Modal */}
      {(showModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-[#0077B6]">
                  {showEditModal ? 'Edit Clinic' : 'Add Clinic'}
                </h2>
                <button 
                  onClick={() => {
                    setShowModal(false);
                    setShowEditModal(false);
                  }} 
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Clinic ID*</label>
                    <input
                      type="text"
                      name="clinicId"
                      value={formData.clinicId}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name (English)*</label>
                    <input
                      type="text"
                      value={formData.name.en}
                      onChange={(e) => handleBilingualChange('name', 'en', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name (Arabic)*</label>
                    <input
                      type="text"
                      value={formData.name.ar}
                      onChange={(e) => handleBilingualChange('name', 'ar', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Specialization (English)*</label>
                    <input
                      type="text"
                      value={formData.specialization.en}
                      onChange={(e) => handleBilingualChange('specialization', 'en', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Specialization (Arabic)*</label>
                    <input
                      type="text"
                      value={formData.specialization.ar}
                      onChange={(e) => handleBilingualChange('specialization', 'ar', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      required
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description (English)*</label>
                    <textarea
                      value={formData.description.en}
                      onChange={(e) => handleBilingualChange('description', 'en', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      required
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description (Arabic)*</label>
                    <textarea
                      value={formData.description.ar}
                      onChange={(e) => handleBilingualChange('description', 'ar', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      required
                    />
                  </div>
                </div>

                {/* Contact Information */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone*</label>
                      <input
                        type="tel"
                        value={formData.contactInfo.phone}
                        onChange={(e) => handleInputChange({ target: { name: 'contactInfo.phone', value: e.target.value } })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email*</label>
                      <input
                        type="email"
                        value={formData.contactInfo.email}
                        onChange={(e) => handleInputChange({ target: { name: 'contactInfo.email', value: e.target.value } })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                      <input
                        type="url"
                        value={formData.contactInfo.website}
                        onChange={(e) => handleInputChange({ target: { name: 'contactInfo.website', value: e.target.value } })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Address</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Street (English)*</label>
                      <input
                        type="text"
                        value={formData.address.street.en}
                        onChange={(e) => handleAddressChange('street', 'en', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Street (Arabic)*</label>
                      <input
                        type="text"
                        value={formData.address.street.ar}
                        onChange={(e) => handleAddressChange('street', 'ar', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City (English)*</label>
                      <input
                        type="text"
                        value={formData.address.city.en}
                        onChange={(e) => handleAddressChange('city', 'en', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City (Arabic)*</label>
                      <input
                        type="text"
                        value={formData.address.city.ar}
                        onChange={(e) => handleAddressChange('city', 'ar', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Country (English)*</label>
                      <input
                        type="text"
                        value={formData.address.country.en}
                        onChange={(e) => handleAddressChange('country', 'en', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Country (Arabic)*</label>
                      <input
                        type="text"
                        value={formData.address.country.ar}
                        onChange={(e) => handleAddressChange('country', 'ar', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Postal Code</label>
                      <input
                        type="text"
                        value={formData.address.postalCode}
                        onChange={(e) => handleAddressChange('postalCode', null, e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      />
                    </div>
                  </div>
                </div>

                {/* Working Hours */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Working Hours</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                      <div key={day}>
                        <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">{day}</label>
                        <input
                          type="text"
                          value={formData.workingHours[day]}
                          onChange={(e) => handleWorkingHoursChange(day, e.target.value)}
                          placeholder="9:00 AM - 5:00 PM"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Services */}
                <div className="border-t pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Services</h3>
                    <button
                      type="button"
                      onClick={addService}
                      className="text-[#20B2AA] hover:text-[#0077B6] font-medium"
                    >
                      + Add Service
                    </button>
                  </div>
                  {formData.services.map((service, index) => (
                    <div key={index} className="flex items-center space-x-4 mb-4">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={service.en}
                          onChange={(e) => handleServiceChange(index, 'en', e.target.value)}
                          placeholder="Service name (English)"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={service.ar}
                          onChange={(e) => handleServiceChange(index, 'ar', e.target.value)}
                          placeholder="Service name (Arabic)"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                        />
                      </div>
                      {formData.services.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeService(index)}
                          className="text-red-600 hover:text-red-800 p-2"
                        >
                          <FaTrash className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* URLs */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Media & Links</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
                      <input
                        type="url"
                        name="image"
                        value={formData.image}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Logo URL</label>
                      <input
                        type="url"
                        name="logo"
                        value={formData.logo}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Map URL</label>
                      <input
                        type="url"
                        name="mapUrl"
                        value={formData.mapUrl}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t">
                  <motion.button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setShowEditModal(false);
                    }}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    type="submit"
                    className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-lg hover:from-blue-600 hover:to-blue-800 font-medium transition-colors shadow-md hover:shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {showEditModal ? 'Update Clinic' : 'Add Clinic'}
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete the clinic "${clinicToDelete?.name?.en || clinicToDelete?.name?.ar || 'Unknown'}"? This action cannot be undone.`}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={successTitle}
        message={successMessage}
      />
    </div>
  );
};

export default Clinics;
